import {type AppBskyVideoDefs} from '@atproto/api'
import {type BskyAgent} from '@atproto/api'
import {type I18n} from '@lingui/core'
import {msg} from '@lingui/macro'
import {nanoid} from 'nanoid/non-secure'

import {AbortError} from '#/lib/async/cancelable'
import {logger} from '#/logger'
import {ServerError} from '#/lib/media/video/errors'
import {type CompressedVideo} from '#/lib/media/video/types'
import {getServiceAuthToken, getVideoUploadLimits} from './upload.shared'
import {createVideoEndpointUrl, mimeToExt} from './util'

export async function uploadVideo({
  video,
  agent,
  did,
  setProgress,
  signal,
  _,
}: {
  video: CompressedVideo
  agent: BskyAgent
  did: string
  setProgress: (progress: number) => void
  signal: AbortSignal
  _: I18n['_']
}) {
  class ChunkedUploadNotSupportedError extends Error {
    constructor(message: string) {
      super(message)
      this.name = 'ChunkedUploadNotSupportedError'
    }
  }

  if (signal.aborted) {
    throw new AbortError()
  }

  // Use the video service for all uploads
  await getVideoUploadLimits(agent, _)

  const uri = createVideoEndpointUrl('/xrpc/app.bsky.video.uploadVideo', {
    did,
    name: `${nanoid(12)}.${mimeToExt(video.mimeType)}`,
  })

  // Log to console for debugging (always visible)
  console.log('🚀 Starting video upload to video service', {
    uri: video.uri.substring(0, 50) + '...',
    mimeType: video.mimeType,
    size: video.size,
    hasBytes: !!video.bytes,
    uploadUrl: uri,
  })
  logger.debug('Starting video upload to video service', {
    uri: video.uri.substring(0, 50) + '...',
    mimeType: video.mimeType,
    size: video.size,
    hasBytes: !!video.bytes,
    uploadUrl: uri,
  })

  // Get initial token - will refresh if upload takes longer than 50 minutes
  let token = await getServiceAuthToken({
    agent,
    lxm: 'com.atproto.repo.uploadBlob',
    exp: Date.now() / 1000 + 60 * 60, // 1 hour (PDS limit - tokens can't expire more than 1 hour in future)
  })
  let tokenExpiry = Date.now() + 50 * 60 * 1000 // Refresh token after 50 minutes

  if (signal.aborted) {
    throw new AbortError()
  }

  // Prepare upload data
  // For large files with blob URLs, we fetch the blob first (blob URLs are already in memory)
  // This prevents browser crashes while maintaining reliable uploads
  let uploadData: ArrayBuffer | Blob | null = null
  
  if (video.bytes) {
    // Small file already in memory - convert ArrayBuffer to Blob for upload
    uploadData = new Blob([video.bytes], {type: video.mimeType})
    console.log('📦 Using in-memory bytes for upload', {size: uploadData.size})
  } else if (video.uri.startsWith('blob:')) {
    // Large file with blob URL - fetch the blob first, then send via XHR
    // Note: blob URLs are already in browser memory, so fetching doesn't duplicate memory
    // We need to fetch it because XHR can't send blob URL strings directly
    if (signal.aborted) {
      throw new AbortError()
    }
    try {
      console.log('📦 Fetching blob from blob URL for XHR upload', {
        blobUrl: video.uri.substring(0, 50),
        size: video.size,
      })
      const response = await fetch(video.uri)
      if (!response.ok) {
        throw new Error(`Failed to fetch blob: ${response.status} ${response.statusText}`)
      }
      uploadData = await response.blob()
      if (!uploadData || uploadData.size === 0) {
        throw new Error('Fetched blob is empty or invalid')
      }
      console.log('✅ Successfully fetched blob from blob URL', {
        blobSize: uploadData.size,
        blobType: uploadData.type,
      })
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : String(e)
      console.error('❌ Error fetching blob from blob URL', {
        error: e,
        errorMessage: errorMsg,
        blobUrl: video.uri.substring(0, 50),
      })
      logger.error('Error fetching blob from blob URL', {
        error: e,
        errorMessage: errorMsg,
        blobUrl: video.uri.substring(0, 50),
      })
      throw new ServerError(_(msg`Failed to load video file for upload: ${errorMsg}. Please try selecting the file again.`))
    }
  } else {
    // For other URI types (data:, file:), fetch as blob
    // Only for smaller files to avoid memory issues
    const fileSize = video.size || 0
    const SAFE_MEMORY_THRESHOLD = 50 * 1024 * 1024 // 50MB - safe to load into memory
    
    if (fileSize > SAFE_MEMORY_THRESHOLD) {
      // File too large to load into memory safely
      // Try to convert to blob URL if possible, otherwise error
      console.warn('⚠️ File too large to load into memory, but not a blob URL', {
        size: fileSize,
        uri: video.uri.substring(0, 50),
      })
      throw new ServerError(_(msg`File is too large to upload from this source. Please try selecting the file again.`))
    }
    
    if (signal.aborted) {
      throw new AbortError()
    }
    try {
      console.log('📦 Fetching video blob from URI (small file)', {
        uriType: video.uri.substring(0, 10),
        size: fileSize,
      })
      const response = await fetch(video.uri)
      if (!response.ok) {
        logger.error('Failed to fetch video blob', {
          status: response.status,
          statusText: response.statusText,
          uri: video.uri.substring(0, 100),
        })
        throw new Error(`Failed to fetch video blob: ${response.status} ${response.statusText}`)
      }
      uploadData = await response.blob()
      if (!uploadData || uploadData.size === 0) {
        logger.error('Fetched blob is empty', {
          blobSize: uploadData?.size || 0,
          originalSize: video.size,
        })
        throw new Error('Fetched blob is empty or invalid')
      }
      console.log('✅ Successfully fetched video blob', {
        blobSize: uploadData.size,
        blobType: uploadData.type,
      })
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : String(e)
      console.error('❌ Error fetching video blob for upload', {
        error: e,
        errorMessage: errorMsg,
        uri: video.uri.substring(0, 100),
        uriType: video.uri.substring(0, 10),
      })
      logger.error('Error fetching video blob for upload', {
        error: e,
        errorMessage: errorMsg,
        uri: video.uri.substring(0, 100),
      })
      throw new ServerError(_(msg`Failed to load video file for upload: ${errorMsg}. The file may have been removed or is no longer accessible. Please try selecting the file again.`))
    }
  }

  if (signal.aborted) {
    throw new AbortError()
  }

  // Note: Standard Bluesky video service (video.bsky.app) does NOT support chunked uploads
  // It expects a single POST with the entire file. However, if you're running a custom
  // video service endpoint that supports Content-Range, we'll attempt chunked uploads
  // for very large files (>100MB) and fall back to single upload if not supported.
  const CHUNK_SIZE = 64 * 1024 * 1024 // 64MB chunks
  const CHUNKED_UPLOAD_THRESHOLD = 100 * 1024 * 1024 // 100MB - only try chunked for very large files
  const fileSize =
    uploadData instanceof Blob
      ? uploadData.size
      : (uploadData as ArrayBuffer).byteLength
  const useChunkedUpload = fileSize > CHUNKED_UPLOAD_THRESHOLD

  if (useChunkedUpload) {
    console.log('📦 Attempting chunked upload (may not be supported by standard Bluesky video service)', {
      fileSize,
      chunkSize: CHUNK_SIZE,
      totalChunks: Math.ceil(fileSize / CHUNK_SIZE),
      uploadUrl: uri,
    })
    try {
      return await uploadVideoChunked({
        video,
        uploadData,
        uri,
        refreshTokenIfNeeded: async () => {
          if (Date.now() >= tokenExpiry) {
            console.log('🔄 Refreshing upload token (expiring soon)')
            token = await getServiceAuthToken({
              agent,
              lxm: 'com.atproto.repo.uploadBlob',
              exp: Date.now() / 1000 + 60 * 60,
            })
            tokenExpiry = Date.now() + 50 * 60 * 1000
            return token
          }
          return token
        },
        setProgress,
        signal,
        _,
        chunkSize: CHUNK_SIZE,
      })
    } catch (e) {
      if (e instanceof ChunkedUploadNotSupportedError) {
        console.warn('⚠️ Chunked upload not supported by server, falling back to single upload', {
          reason: e.message,
        })
      } else {
        throw e
      }
    }
  }

  // Use XHR for single upload (standard Bluesky video service expects entire file in one request)
  // For large files, this will be the fallback if chunked uploads aren't supported
  // Note: Standard Bluesky PDS/video service does NOT support Content-Range chunked uploads
  // It expects a single POST with the entire file. If you've modified your PDS to support
  // chunked uploads, the chunked attempt above will work, otherwise it falls back here.
  
  // Function to refresh token if needed
  const refreshTokenIfNeeded = async () => {
    if (Date.now() >= tokenExpiry) {
      console.log('🔄 Refreshing upload token (expiring soon)')
      token = await getServiceAuthToken({
        agent,
        lxm: 'com.atproto.repo.uploadBlob',
        exp: Date.now() / 1000 + 60 * 60,
      })
      tokenExpiry = Date.now() + 50 * 60 * 1000
    }
    return token
  }
  
  // Chunked upload function for large files (Content-Range)
  // This will only work if your PDS/video service supports Content-Range headers
  async function uploadVideoChunked({
    video,
    uploadData,
    uri,
    refreshTokenIfNeeded,
    setProgress,
    signal,
    _,
    chunkSize,
  }: {
    video: CompressedVideo
    uploadData: Blob | ArrayBuffer
    uri: string
    refreshTokenIfNeeded: () => Promise<string>
    setProgress: (progress: number) => void
    signal: AbortSignal
    _: I18n['_']
    chunkSize: number
  }): Promise<AppBskyVideoDefs.JobStatus> {
    const blob =
      uploadData instanceof Blob
        ? uploadData
        : new Blob([uploadData], {type: video.mimeType})
    const fileSize = blob.size
    const totalChunks = Math.ceil(fileSize / chunkSize)

    console.log('🚀 Starting chunked upload', {
      fileSize,
      chunkSize,
      totalChunks,
    })

    const maxRetries = 3
    let offset = 0

    const parseRangeEnd = (rangeHeader: string | null) => {
      if (!rangeHeader) return null
      const match = rangeHeader.match(/bytes=(\d+)-(\d+)/)
      if (!match) return null
      return Number(match[2])
    }

    while (offset < fileSize) {
      if (signal.aborted) {
        throw new AbortError()
      }

      const chunkEnd = Math.min(offset + chunkSize, fileSize)
      const chunk = blob.slice(offset, chunkEnd, video.mimeType)
      const chunkStart = offset
      const chunkEndInclusive = chunkEnd - 1

      let attempt = 0
      while (attempt < maxRetries) {
        if (signal.aborted) {
          throw new AbortError()
        }

        const currentToken = await refreshTokenIfNeeded()
        const result = await new Promise<{
          status: number
          responseText: string
          rangeHeader: string | null
        }>((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.timeout = 12 * 60 * 60 * 1000 // 12 hours

          xhr.upload.addEventListener('progress', e => {
            if (e.lengthComputable) {
              const progress = (chunkStart + e.loaded) / fileSize
              setProgress(progress)
            }
          })

          xhr.onloadend = () => {
            if (signal.aborted) {
              reject(new AbortError())
              return
            }
            if (xhr.readyState !== 4) {
              reject(new ServerError(_(msg`Upload connection failed.`)))
              return
            }
            resolve({
              status: xhr.status,
              responseText: xhr.responseText ?? '',
              rangeHeader: xhr.getResponseHeader('Range'),
            })
          }

          xhr.onerror = () => {
            reject(new ServerError(_(msg`Network error during upload.`)))
          }

          xhr.ontimeout = () => {
            reject(new ServerError(_(msg`Upload timeout.`)))
          }

          xhr.open('POST', uri)
          xhr.setRequestHeader('Content-Type', video.mimeType)
          xhr.setRequestHeader('Authorization', `Bearer ${currentToken}`)
          xhr.setRequestHeader(
            'Content-Range',
            `bytes ${chunkStart}-${chunkEndInclusive}/${fileSize}`,
          )
          xhr.send(chunk)
        })

        if (result.status === 308) {
          const serverEnd = parseRangeEnd(result.rangeHeader)
          if (serverEnd !== null) {
            offset = serverEnd + 1
          } else {
            offset = chunkEnd
          }
          break
        }

        if (result.status >= 200 && result.status < 300) {
          offset = chunkEnd
          if (offset >= fileSize) {
            try {
              const uploadRes = JSON.parse(
                result.responseText,
              ) as AppBskyVideoDefs.JobStatus
              return uploadRes
            } catch (e) {
              throw new ServerError(_(msg`Failed to parse upload response.`))
            }
          }
          break
        }

        if (attempt === 0 && (result.status === 400 || result.status === 404 || result.status === 405)) {
          throw new ChunkedUploadNotSupportedError(
            `Server rejected Content-Range (status ${result.status})`,
          )
        }

        attempt++
        if (attempt >= maxRetries) {
          throw new ServerError(_(msg`Upload failed with status ${result.status}.`))
        }
        await new Promise(resolve =>
          setTimeout(resolve, Math.min(1000 * Math.pow(2, attempt), 10000)),
        )
      }
    }

    throw new ServerError(_(msg`Upload failed to complete.`))
  }

  // For large files, implement retry logic for network errors
  // Standard Bluesky video service doesn't support chunked uploads, so we need
  // to retry the entire upload if it fails due to network issues
  const isLargeFile = fileSize > 50 * 1024 * 1024 // 50MB
  const maxRetries = isLargeFile ? 2 : 1 // Retry large files once more
  let retryCount = 0
  
  while (retryCount <= maxRetries) {
    if (signal.aborted) {
      throw new AbortError()
    }
    
    try {
      const currentToken = await refreshTokenIfNeeded()
      const res = await new Promise<AppBskyVideoDefs.JobStatus>(
        (resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.timeout = 12 * 60 * 60 * 1000 // 12 hours
          
          // Track upload start time for debugging
          const uploadStartTime = Date.now()
          let lastProgressTime = uploadStartTime
          let lastProgressBytes = 0
          
          xhr.upload.addEventListener('progress', async e => {
        if (e.lengthComputable) {
          const progress = e.loaded / e.total
          setProgress(progress)
          
          // Track progress for stall detection
          const now = Date.now()
          const timeSinceLastProgress = now - lastProgressTime
          const bytesSinceLastProgress = e.loaded - lastProgressBytes
          
          // Log if progress stalls for more than 30 seconds
          if (timeSinceLastProgress > 30000 && bytesSinceLastProgress === 0) {
            console.warn('⚠️ Upload progress stalled', {
              stalledFor: timeSinceLastProgress,
              loaded: e.loaded,
              total: e.total,
              percent: ((e.loaded / e.total) * 100).toFixed(2) + '%',
            })
          }
          
          lastProgressTime = now
          lastProgressBytes = e.loaded
          
          // Refresh token if needed during upload
          await refreshTokenIfNeeded()
        }
      })
      
      xhr.onloadend = () => {
        if (signal.aborted) {
          reject(new AbortError())
        } else if (xhr.readyState === 4) {
          logger.debug('Upload response received', {
            status: xhr.status,
            statusText: xhr.statusText,
            responseLength: xhr.responseText?.length || 0,
          })
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const uploadRes = JSON.parse(
                xhr.responseText,
              ) as AppBskyVideoDefs.JobStatus
              logger.debug('Upload successful', {jobId: uploadRes.jobId})
              resolve(uploadRes)
            } catch (e) {
              const errorMsg = e instanceof Error ? e.message : String(e)
              logger.error('Failed to parse upload response', {
                error: e,
                responseText: xhr.responseText?.substring(0, 200),
              })
              reject(new ServerError(_(msg`Failed to parse upload response: ${errorMsg}`)))
            }
          } else {
            let errorDetails = ''
            try {
              const errorResponse = JSON.parse(xhr.responseText)
              errorDetails = errorResponse.error || errorResponse.message || ''
            } catch {
              errorDetails = xhr.responseText || ''
            }
            const statusText = xhr.statusText || 'Unknown error'
            console.error('❌ Upload failed with HTTP error', {
              status: xhr.status,
              statusText,
              errorDetails: errorDetails.substring(0, 200),
              responseText: xhr.responseText?.substring(0, 500),
              uploadUrl: uri,
            })
            logger.error('Upload failed with HTTP error', {
              status: xhr.status,
              statusText,
              errorDetails: errorDetails.substring(0, 200),
            })
            reject(new ServerError(_(msg`Upload failed with status ${xhr.status}: ${statusText}${errorDetails ? `. ${errorDetails}` : ''}`)))
          }
        } else {
          logger.error('Upload connection failed', {readyState: xhr.readyState})
          reject(new ServerError(_(msg`Upload connection failed. Please check your internet connection and try again.`)))
        }
      }
      
      xhr.onerror = (event) => {
        const uploadDuration = Date.now() - uploadStartTime
        const errorDetails = {
          status: xhr.status,
          statusText: xhr.statusText,
          readyState: xhr.readyState,
          responseText: xhr.responseText?.substring(0, 500),
          uploadUrl: uri,
          uploadDuration: `${(uploadDuration / 1000).toFixed(2)}s`,
          timestamp: new Date().toISOString(),
          event: event,
        }
        console.error('❌ XHR upload error (onerror):', errorDetails)
        let uploadDataInfo: {type: string; size?: number}
        const data = uploadData as Blob | ArrayBuffer | null
        if (data instanceof Blob) {
          uploadDataInfo = {type: 'Blob', size: data.size}
        } else if (data instanceof ArrayBuffer) {
          uploadDataInfo = {type: 'ArrayBuffer', size: data.byteLength}
        } else {
          uploadDataInfo = {type: data === null ? 'null' : 'unknown'}
        }
        console.error('Full error context:', {
          ...errorDetails,
          uploadDataInfo,
        })
        logger.error('XHR upload error', errorDetails)
        
        let errorMessage = 'Network error during upload.'
        if (xhr.status === 0 && xhr.readyState === 0) {
          errorMessage = 'CORS or network connection error. The upload server may be unreachable or blocking the request.'
        } else if (xhr.status > 0) {
          errorMessage = `Network error: HTTP ${xhr.status} ${xhr.statusText || ''}`
        }
        
        // Add more context about when the error occurred
        if (uploadDuration > 60000) {
          errorMessage += ` Upload was running for ${(uploadDuration / 1000 / 60).toFixed(1)} minutes before failing.`
        }
        
        // For network errors on large files, allow retry
        if (isLargeFile && retryCount < maxRetries) {
          console.warn('⚠️ Network error on large file upload, will retry', {
            retryCount,
            maxRetries,
            uploadDuration: `${(uploadDuration / 1000).toFixed(2)}s`,
          })
          // Don't reject immediately - let the retry logic handle it
          reject(new Error('NETWORK_ERROR_RETRY'))
        } else {
          reject(new ServerError(_(msg`${errorMessage} Please check your internet connection and try again.`)))
        }
      }
      
      xhr.ontimeout = () => {
        const uploadDuration = Date.now() - uploadStartTime
        console.error('❌ XHR upload timeout', {
          timeout: xhr.timeout,
          uploadDuration: `${(uploadDuration / 1000).toFixed(2)}s`,
          timestamp: new Date().toISOString(),
          retryCount,
          maxRetries,
        })
        // For timeout errors on large files, allow retry
        if (isLargeFile && retryCount < maxRetries) {
          console.warn('⚠️ Upload timeout on large file, will retry', {
            retryCount,
            maxRetries,
          })
          reject(new Error('TIMEOUT_RETRY'))
        } else {
          reject(new ServerError(_(msg`Upload timeout after ${(uploadDuration / 1000 / 60).toFixed(1)} minutes. The file may be too large or the connection is too slow. Please try again.`)))
        }
      }
      
      xhr.open('POST', uri)
      xhr.setRequestHeader('Content-Type', video.mimeType)
      xhr.setRequestHeader('Authorization', `Bearer ${currentToken}`)
      
      // Log upload details
      if (uploadData) {
        const size = uploadData instanceof Blob 
          ? uploadData.size 
          : (uploadData as ArrayBuffer).byteLength
        const dataType = uploadData instanceof Blob ? 'Blob' : 'ArrayBuffer'
        console.log('📤 Sending upload request via XHR (in-memory)', {
          uploadDataSize: size,
          uploadDataType: dataType,
          mimeType: video.mimeType,
          uploadUrl: uri,
          timestamp: new Date().toISOString(),
        })
      }
      
      try {
        // XHR can send Blob or ArrayBuffer directly
        // Note: We've already converted blob URLs to Blob objects above
        xhr.send(uploadData)
        console.log('✅ XHR.send() called successfully', {
          timestamp: new Date().toISOString(),
        })
      } catch (sendError) {
        const errorMsg = sendError instanceof Error ? sendError.message : String(sendError)
        console.error('❌ Error calling xhr.send():', {
          error: sendError,
          errorMessage: errorMsg,
          uploadDataType: typeof uploadData,
          timestamp: new Date().toISOString(),
        })
        logger.error('Error calling xhr.send()', {error: sendError})
        reject(new ServerError(_(msg`Failed to send upload request: ${errorMsg}`)))
      }
    },
  )
  
      // If we get here, upload succeeded
      if (!res.jobId) {
        throw new ServerError(res.error || _(msg`Failed to upload video`))
      }
      
      if (signal.aborted) {
        throw new AbortError()
      }
      return res
    } catch (e) {
      const isRetryableError = e instanceof Error && 
        (e.message === 'NETWORK_ERROR_RETRY' || e.message === 'TIMEOUT_RETRY')
      
      if (isRetryableError && retryCount < maxRetries) {
        retryCount++
        const backoffMs = Math.min(2000 * retryCount, 10000) // 2s, 4s, max 10s
        console.log(`🔄 Retrying upload (attempt ${retryCount + 1}/${maxRetries + 1}) after ${backoffMs}ms`, {
          error: e instanceof Error ? e.message : String(e),
        })
        await new Promise(resolve => setTimeout(resolve, backoffMs))
        continue // Retry the upload
      }
      
      // Not retryable or max retries reached
      throw e
    }
  }
  
  throw new ServerError(_(msg`Upload failed after ${maxRetries + 1} attempts`))
}
